import React, { useCallback, FC } from 'react'
import { Field, FieldArray, Form, FormikProps } from 'formik'
import InputField from '@components/forms/components/InputField'
import SelectField from '@components/forms/components/SelectField'
import SwitchField from '@components/forms/components/SwitchField'
import CommonErrorField from '@components/forms/components/CommonErrorField'
import TextAreaField from '@components/forms/components/TextAreaField'
import ImageArrayField from './components/ImageArrayField'
import RefNumbersField from './components/RefNumbersField'
import ProductCodesField from './components/ProductCodesField'
import { Product, ProductType } from '@typings/entities/Product'
import { Trans } from '@lingui/react'
import { t } from '@lingui/macro'
import { Ability, withAcl } from '@components/blocks/Acl'
import DimensionsField from './components/DimensionsField'
import { Col } from '@designSystem/Grid'
import { ExternalFormProps } from './types'
import StocksTabWrap from './components/StocksTabWrap'
import Modal from '@mailstep/design-system/ui/Blocks/Modal'
import SwitchWithDialogField from '../components/SwitchWithDialogField'
import { ResponsiveRowWrap, ResponsiveSectionWrap } from '../styles'
import { localisationOptions } from '@constants/localisation'
import { getPackagingTypeOptions, salesabilityOptions } from './constants'
import { StyledSubTitle, StyledSubLabel } from '@components/elements/Typography/lib'

type Props = ExternalFormProps &
  FormikProps<Product> & {
    ability: Ability
    setType: React.Dispatch<React.SetStateAction<ProductType>>
    onDownloadAttachment: (id: string, fileName?: string) => Promise<void>
    workAroundLot?: boolean
  }

const ProductEditForm: FC<Props> = ({
  handleSubmit,
  isSubmitting,
  createView = false,
  productGroupOptions,
  productServiceOptions,
  productFeaturesOptions,
  createProductGroup,
  onDownloadAttachment,
  ability,
  countryOptions,
  eshopOptions,
  isSecondaryLoading,
  productStocks,
  title,
  titleIcon,
  onCancel,
  openLightbox,
  setFieldValue,
  setType,
  isLotEvidenceEditable,
  workAroundLot,
  hasCodes,
  hasQuantityStocks,
}) => {
  // all fields are enabled in create view
  const isAdminDisabled = !createView && !ability.can('adminEdit', 'Product') // assuming at least 'edit' is granted, since we are on this page
  const isSupervisorDisabled = !createView && !ability.can('supervisorEdit', 'Product')
  const clearProductSku = useCallback(() => {
    if (
      confirm(
        t({
          id: 'form.productSku.confirmDeletion',
          message: 'Do you really want to remove this product SKU?',
        }),
      )
    ) {
      setFieldValue('productSku', null)
    }
  }, [setFieldValue])

  return (
    <Modal
      title={title}
      titleIcon={titleIcon}
      onCancel={onCancel}
      cancelLabel={<Trans id="form.buttonCancel" message="Cancel" />}
      hasFooter={false}
      width="wide"
      isLoading={isSubmitting}
    >
      <StocksTabWrap
        createView={createView}
        productStocks={productStocks}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        setType={setType}
        setFieldValue={setFieldValue}
      >
        <Form onSubmit={handleSubmit}>
          <CommonErrorField />
          <ResponsiveSectionWrap>
            <Col mt="32px" mr={3}>
              <ResponsiveRowWrap>
                <Field
                  component={InputField}
                  spaceAround
                  label={<Trans id="form.productName.label" message="Name" />}
                  name="name"
                />
              </ResponsiveRowWrap>
              <ResponsiveRowWrap>
                <Field
                  component={SelectField}
                  label={<Trans id="form.eshops.label" message="Eshops" />}
                  name="eshops"
                  options={eshopOptions}
                  isMulti
                  spaceAround
                  isSearchable={false}
                  isLoading={isSecondaryLoading}
                />
              </ResponsiveRowWrap>

              <StyledSubTitle mb="8px">
                <Trans id="codes" message="Codes" />
                <StyledSubLabel>
                  {hasQuantityStocks && (
                    <>
                      {' '}
                      (<Trans id="productHasStockCodesCannotBeEdited" message="Code of product with stock, cannot be edited" />)
                    </>
                  )}
                </StyledSubLabel>
              </StyledSubTitle>

              <ResponsiveRowWrap>
                <Field
                  name="productSku"
                  render={(props: { form: { values: { codes?: { value: string }[]; productSku?: string } }; field: object }) => (
                    <InputField
                      {...props}
                      spaceAround
                      label={<Trans id="form.productSku.label" message="Product SKU" />}
                      disabled={isSupervisorDisabled || hasQuantityStocks || props.form.values.codes?.length > 0 || hasCodes}
                      onClear={
                        isSupervisorDisabled ||
                        hasQuantityStocks ||
                        !props.form.values.codes?.find((code: { value: string }) => code.value === props.form.values.productSku)
                          ? undefined
                          : () => clearProductSku()
                      }
                    />
                  )}
                />
                <Field
                  component={InputField}
                  label={<Trans id="form.internalSku.label" message="Internal SKU" />}
                  name="internalSku"
                  spaceAround
                  disabled={isSupervisorDisabled}
                />
              </ResponsiveRowWrap>

              <FieldArray
                name="codes"
                render={(props: { name: string; form: { values: { productSku?: string } } }) => (
                  <ProductCodesField
                    {...props}
                    min={hasCodes ? 1 : 0}
                    canEdit={
                      (!isAdminDisabled && !hasQuantityStocks && !(props.form.values.productSku && hasCodes)) || createView
                    }
                    canRemove={
                      (!isAdminDisabled && !hasQuantityStocks && !(props.form.values.productSku && hasCodes)) || createView
                    }
                    canAdd={!createView || (createView && !props.form.values.productSku)}
                    label={<Trans id="form.codes.label" message="Codes" />}
                  />
                )}
              />

              <StyledSubTitle mb="8px">
                <Trans id="form.productReferenceNumbers.label" message="Reference numbers" />
              </StyledSubTitle>
              <FieldArray
                name="referenceNumbers"
                render={useCallback(
                  (props: { name: string }) => (
                    <RefNumbersField {...props} disabled={isAdminDisabled} />
                  ),
                  [isAdminDisabled],
                )}
              />

              <ResponsiveRowWrap>
                <Col size={6}>
                  <Field
                    component={SwitchField}
                    label={<Trans id="form.active.label" message="Active" />}
                    name="active"
                    labelJustify="between"
                    labelPosition="right"
                    disabled={createView}
                    spaceBetween
                  />
                  <Field
                    component={createView ? SwitchField : SwitchWithDialogField}
                    label={<Trans id="form.workAroundSnIn.label" message="Serial number evidence" />}
                    labelPosition="right"
                    labelJustify="between"
                    name="workAroundSnIn"
                    spaceBetween
                    dialogText={
                      <Trans
                        id="cornerDialog.workAroundSnIn.text"
                        message="If the product has been already expedited without an serial number evidence, it may cause problems with his return."
                      />
                    }
                  />
                  <Field
                    component={SwitchField}
                    label={<Trans id="form.workAroundLot.label" message="LOT evidence" />}
                    name="workAroundLot"
                    labelJustify="between"
                    labelPosition="right"
                    disabled={!isLotEvidenceEditable && !createView}
                    spaceBetween
                  />
                </Col>
                <Col size={6}>
                  <Field
                    component={SwitchField}
                    label={<Trans id="form.additionalWork.label" message="Additional work on receiving" />}
                    labelPosition="right"
                    labelJustify="between"
                    name="requiresAdditionalWork"
                    spaceBetween
                  />
                  <Field
                    component={SwitchField}
                    label={<Trans id="form.qualitativeReceiving.label" message="Qualitative receiving" />}
                    labelPosition="right"
                    labelJustify="between"
                    name="requiresQualitativeReceiving"
                    spaceBetween
                  />
                </Col>
              </ResponsiveRowWrap>

              <ResponsiveRowWrap>
                <Col mt="16px">
                  <Field
                    component={TextAreaField}
                    label={<Trans id="form.description.label" message="Description" />}
                    name="description"
                    spaceAround
                    rows={2}
                  />
                </Col>
              </ResponsiveRowWrap>
              <ResponsiveRowWrap>
                <Col>
                  <Field
                    component={TextAreaField}
                    label={<Trans id="form.customsDescription.label" message="Customs description" />}
                    name="customsDescription"
                    spaceAround
                    rows={2}
                  />
                </Col>
              </ResponsiveRowWrap>
            </Col>
            <Col>
              <ResponsiveRowWrap>
                <DimensionsField title={<Trans id="form.dimensions.heading" message="Dimensions" />} />
                <DimensionsField
                  name="int"
                  isDisabled={true}
                  title={<Trans id="form.measurements.heading" message="Measurements" />}
                />
              </ResponsiveRowWrap>
              <ResponsiveRowWrap>
                <Field
                  component={SelectField}
                  label={<Trans id="form.languagesOnTheCoverLabel" message="Languages on the cover" />}
                  name="localisation"
                  options={localisationOptions}
                  isClearable
                  isMulti
                  spaceAround
                  isSearchable
                />
              </ResponsiveRowWrap>
              <ResponsiveRowWrap>
                <Field
                  component={SelectField}
                  label={<Trans id="form.productServices" message="Product services" />}
                  name="services"
                  options={productServiceOptions}
                  isClearable
                  isMulti
                  spaceAround
                  isSearchable
                />
              </ResponsiveRowWrap>
              <ResponsiveRowWrap>
                <Field
                  component={SelectField}
                  label={<Trans id="form.productFeatures" message="Product features" />}
                  name="features"
                  options={productFeaturesOptions}
                  isClearable
                  isMulti
                  spaceAround
                  isSearchable
                />
              </ResponsiveRowWrap>
              <ResponsiveRowWrap>
                <Field
                  component={InputField}
                  spaceAround
                  label={<Trans id="form.hsCode.label" message="HS Code" />}
                  name="hsCode"
                />
                <Field
                  component={SelectField}
                  label={<Trans id="form.countryOfOrigin.label" message="Country of origin" />}
                  name="countryOfOrigin"
                  spaceAround
                  options={countryOptions}
                  isClearable
                  isLoading={isSecondaryLoading}
                />
              </ResponsiveRowWrap>

              <ResponsiveRowWrap>
                <Field
                  component={SelectField}
                  label={<Trans id="form.packagingType.label" message="Packaging type" />}
                  name="packagingType"
                  options={getPackagingTypeOptions()}
                  disabled={isAdminDisabled}
                  isSearchable={false}
                  spaceAround
                  isClearable
                  placeholder={t({ id: 'form.packagingType.emptyOption', message: 'Choose packaging type' })}
                />
                <Field
                  component={SelectField}
                  onCreateOption={createProductGroup}
                  options={productGroupOptions}
                  label={<Trans id="form.productGroup.label" message="Product group" />}
                  name="productGroup"
                  isClearable
                  spaceAround
                  isLoading={isSecondaryLoading}
                />
              </ResponsiveRowWrap>

              <ResponsiveRowWrap>
                <Field
                  component={SelectField}
                  label={<Trans id="form.salesability.label" message="Salesability" />}
                  name="expectedTurnover"
                  options={salesabilityOptions}
                />
                <Field
                  component={InputField}
                  type="number"
                  label={<Trans id="form.expirationOffset.label" message="Expiration offset" />}
                  name="expirationOffset"
                  disabled={!workAroundLot}
                />
                <Field
                  component={InputField}
                  type="number"
                  label={<Trans id="form.inboundExpirationOffset.label" message="Inbound expiration offset" />}
                  name="inboundExpirationOffset"
                  disabled={!workAroundLot}
                />
                <Field
                  component={InputField}
                  type="number"
                  label={<Trans id="form.outboundExpirationOffset.label" message="Outbound expiration offset" />}
                  name="outboundExpirationOffset"
                  disabled={!workAroundLot}
                />
              </ResponsiveRowWrap>

              <StyledSubTitle mt="8px">
                <Trans id="images" message="Images" />
              </StyledSubTitle>

              <ResponsiveRowWrap>
                <Col size={6}>
                  <FieldArray
                    name="images"
                    render={useCallback(
                      (props: { name: string }) => (
                        <ImageArrayField
                          {...props}
                          onDownload={onDownloadAttachment}
                          openLightbox={openLightbox}
                          spaceAround
                          label={<Trans id="form.productImages.label" message="Your images" />}
                        />
                      ),
                      [openLightbox, onDownloadAttachment],
                    )}
                  />
                </Col>
                <Col size={6}>
                  <FieldArray
                    name="intImages"
                    render={useCallback(
                      (props: { name: string }) => (
                        <ImageArrayField
                          {...props}
                          onDownload={onDownloadAttachment}
                          openLightbox={openLightbox}
                          spaceAround
                          label={<Trans id="form.productWmsImages.label" message="Internal images" />}
                          disabled
                        />
                      ),
                      [openLightbox, onDownloadAttachment],
                    )}
                  />
                </Col>
              </ResponsiveRowWrap>
            </Col>
          </ResponsiveSectionWrap>
        </Form>
      </StocksTabWrap>
    </Modal>
  )
}

export default withAcl(ProductEditForm)
